import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { primary, primaryHover } from "shared/theme/trodoColors";

/** Trodo brand (aligned with analytics studio modals) */
const BRAND = {
  primary,
  primaryHover,
  text: "var(--text-primary)",
  muted: "var(--text-secondary)",
  border: "var(--border-default)",
  overlay: "rgba(15, 23, 42, 0.12)",
  danger: "var(--feedback-error)",
  font: "Inter, sans-serif",
};

/**
 * Above MUI's Dialog (default z-index 1300) and the members overlay (1200).
 *
 * These dialogs are almost always launched FROM something already stacked —
 * "remove member" and "cancel invite" sit inside the Members panel, which can
 * itself be inside the invite modal. At the old z-[300] the confirm rendered
 * *behind* whatever opened it: the screen dimmed, the app stopped responding,
 * and the prompt asking you to approve the removal was invisible.
 *
 * A confirm must outrank its opener by construction — it is always the most
 * recent thing the user asked for. Kept below MUI's tooltip layer (1500) so
 * tooltips still surface over it.
 */
const Z_BACKDROP = 1400;
const Z_PANEL = 1401;

function DialogShell({ children, onBackdropClose, wide }) {
  return (
    <>
      <div
        className="fixed inset-0"
        style={{
          backgroundColor: BRAND.overlay,
          zIndex: Z_BACKDROP,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
        aria-hidden
        onMouseDown={onBackdropClose}
      />
      <div
        className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border shadow-lg ${
          wide ? "w-[min(440px,calc(100vw-2rem))]" : "w-[min(400px,calc(100vw-2rem))]"
        }`}
        style={{
          fontFamily: BRAND.font,
          backgroundColor: "var(--surface-base)",
          borderColor: BRAND.border,
          zIndex: Z_PANEL,
        }}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
}

function AlertView({ title, message, variant, confirmLabel, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isError = variant === "error";
  return createPortal(
    <DialogShell onBackdropClose={onClose}>
      <div className="px-5 pt-5 pb-4">
        <h2 className="text-[15px] font-semibold" style={{ color: isError ? BRAND.danger : BRAND.text }}>
          {title}
        </h2>
        {message ? (
          <p className="text-[13px] mt-1.5 leading-relaxed whitespace-pre-wrap" style={{ color: BRAND.muted }}>
            {message}
          </p>
        ) : null}
      </div>
      <div className="flex justify-end px-5 pb-4">
        <button
          type="button"
          className="px-4 py-2 text-[13px] font-medium rounded-lg text-ink-onBrand transition-colors"
          style={{ backgroundColor: BRAND.primary }}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = BRAND.primaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = BRAND.primary;
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </DialogShell>,
    document.body
  );
}

function ConfirmView({ title, message, confirmLabel, cancelLabel, danger, onCancel, onConfirm }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <DialogShell onBackdropClose={onCancel}>
      <div className="px-5 pt-5 pb-4">
        <h2 className="text-[15px] font-semibold" style={{ color: BRAND.text }}>
          {title}
        </h2>
        {message ? (
          <p className="text-[13px] mt-1.5 leading-relaxed whitespace-pre-wrap" style={{ color: BRAND.muted }}>
            {message}
          </p>
        ) : null}
      </div>
      <div className="flex justify-end gap-2 px-5 pb-4">
        <button
          type="button"
          className="px-4 py-2 text-[13px] font-medium rounded-lg transition-colors"
          style={{ color: BRAND.muted }}
          onClick={onCancel}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--surface-sunken)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="px-4 py-2 text-[13px] font-medium rounded-lg text-ink-onBrand transition-colors"
          style={{
            backgroundColor: danger ? BRAND.danger : BRAND.primary,
          }}
          onClick={onConfirm}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = danger ? "var(--feedback-error)" : BRAND.primaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = danger ? BRAND.danger : BRAND.primary;
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </DialogShell>,
    document.body
  );
}

function PromptView({ title, message, placeholder, defaultValue, confirmLabel, cancelLabel, onCancel, onConfirm }) {
  const [value, setValue] = useState(defaultValue || "");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <DialogShell wide onBackdropClose={onCancel}>
      <div className="px-5 pt-5 pb-4">
        <h2 className="text-[15px] font-semibold" style={{ color: BRAND.text }}>
          {title}
        </h2>
        {message ? (
          <p className="text-[13px] mt-1.5 leading-relaxed" style={{ color: BRAND.muted }}>
            {message}
          </p>
        ) : null}
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onConfirm(value);
            }
          }}
          autoFocus
          placeholder={placeholder || ""}
          className="w-full mt-3 px-3 py-2 rounded-lg border text-[13px] outline-none transition-colors"
          style={{
            borderColor: BRAND.border,
            color: BRAND.text,
            backgroundColor: "var(--surface-base)",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = BRAND.primary;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = BRAND.border;
          }}
        />
      </div>
      <div className="flex justify-end gap-2 px-5 pb-4">
        <button
          type="button"
          className="px-4 py-2 text-[13px] font-medium rounded-lg transition-colors"
          style={{ color: BRAND.muted }}
          onClick={onCancel}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "var(--surface-sunken)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className="px-4 py-2 text-[13px] font-medium rounded-lg text-ink-onBrand transition-colors"
          style={{ backgroundColor: BRAND.primary }}
          onClick={() => onConfirm(value)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = BRAND.primaryHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = BRAND.primary;
          }}
        >
          {confirmLabel}
        </button>
      </div>
    </DialogShell>,
    document.body
  );
}

/** "Untitled…" is not a name: nothing is created under one. */
const isPlaceholderName = (n) => !String(n || "").trim() || /^untitled\b/i.test(String(n).trim());

const fieldStyle = { borderColor: BRAND.border, color: BRAND.text, backgroundColor: "var(--surface-base)" };
const focusBrand = (e) => { e.currentTarget.style.borderColor = BRAND.primary; };
const blurBrand = (e) => { e.currentTarget.style.borderColor = BRAND.border; };

/** A name and an optional description — what a new thing needs before it exists. */
function DetailsView({ title, namePlaceholder, descriptionPlaceholder, defaultName, defaultDescription, withDescription = true, confirmLabel, cancelLabel, onCancel, onConfirm }) {
  const [name, setName] = useState(defaultName || "");
  const [description, setDescription] = useState(defaultDescription || "");
  const invalid = isPlaceholderName(name);
  const submit = () => { if (!invalid) onConfirm({ name: name.trim(), description: description.trim() }); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return createPortal(
    <DialogShell wide onBackdropClose={onCancel}>
      <form className="px-5 pt-5 pb-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <h2 className="text-[15px] font-semibold" style={{ color: BRAND.text }}>{title}</h2>
        <label className="mt-4 block text-[12px] font-medium" style={{ color: BRAND.muted }} htmlFor="app-dialog-name">Name</label>
        <input
          id="app-dialog-name" type="text" value={name} maxLength={120} autoFocus
          onChange={(e) => setName(e.target.value)} placeholder={namePlaceholder || ""}
          className="w-full mt-1 px-3 py-2 rounded-lg border text-[13px] outline-none transition-colors"
          style={fieldStyle} onFocus={focusBrand} onBlur={blurBrand}
        />
        {withDescription ? (<>
        <label className="mt-3 block text-[12px] font-medium" style={{ color: BRAND.muted }} htmlFor="app-dialog-description">
          Description <span className="font-normal">(optional)</span>
        </label>
        <textarea
          id="app-dialog-description" rows={3} value={description} maxLength={2000}
          onChange={(e) => setDescription(e.target.value)} placeholder={descriptionPlaceholder || ""}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          className="w-full mt-1 px-3 py-2 rounded-lg border text-[13px] leading-relaxed outline-none transition-colors resize-none"
          style={fieldStyle} onFocus={focusBrand} onBlur={blurBrand}
        />
        </>) : null}
        <div className="flex justify-end gap-2 pt-4">
          <button type="button" className="px-4 py-2 text-[13px] font-medium rounded-lg transition-colors hover:bg-[color:var(--surface-sunken)]"
            style={{ color: BRAND.muted }} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button type="submit" disabled={invalid}
            className="px-4 py-2 text-[13px] font-medium rounded-lg text-ink-onBrand transition-opacity disabled:opacity-50"
            style={{ backgroundColor: BRAND.primary }}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </DialogShell>,
    document.body
  );
}

const AppDialogContext = createContext(null);

/**
 * Global branded dialogs (replaces window.alert / confirm / prompt).
 */
export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);

  const alertFn = useCallback((message, options = {}) => {
    return new Promise((resolvePromise) => {
      setDialog({
        kind: "alert",
        message: String(message),
        title: options.title || "Notice",
        variant: options.variant || "default",
        confirmLabel: options.confirmLabel || "OK",
        close: () => {
          resolvePromise();
          setDialog(null);
        },
      });
    });
  }, []);

  const confirmFn = useCallback((message, options = {}) => {
    return new Promise((resolvePromise) => {
      setDialog({
        kind: "confirm",
        message: String(message),
        title: options.title || "Confirm",
        confirmLabel: options.confirmLabel || "OK",
        cancelLabel: options.cancelLabel || "Cancel",
        danger: !!options.danger,
        close: (result) => {
          resolvePromise(result);
          setDialog(null);
        },
      });
    });
  }, []);

  const promptFn = useCallback((message, options = {}) => {
    return new Promise((resolvePromise) => {
      setDialog({
        kind: "prompt",
        message: message != null ? String(message) : "",
        title: options.title || "Enter value",
        placeholder: options.placeholder,
        defaultValue: options.defaultValue || "",
        confirmLabel: options.confirmLabel || "OK",
        cancelLabel: options.cancelLabel || "Cancel",
        close: (result) => {
          resolvePromise(result);
          setDialog(null);
        },
      });
    });
  }, []);

  // details(title, options) → { name, description } or null when cancelled.
  const detailsFn = useCallback((title, options = {}) => {
    return new Promise((resolvePromise) => {
      setDialog({
        kind: "details",
        title: title || "New",
        namePlaceholder: options.namePlaceholder || "",
        descriptionPlaceholder: options.descriptionPlaceholder || "",
        defaultName: options.defaultName || "",
        defaultDescription: options.defaultDescription || "",
        withDescription: options.description !== false,
        confirmLabel: options.confirmLabel || "Create",
        cancelLabel: options.cancelLabel || "Cancel",
        close: (result) => {
          resolvePromise(result);
          setDialog(null);
        },
      });
    });
  }, []);

  const value = useMemo(
    () => ({
      alert: alertFn,
      confirm: confirmFn,
      prompt: promptFn,
      details: detailsFn,
    }),
    [alertFn, confirmFn, promptFn, detailsFn]
  );

  let overlay = null;
  if (dialog && typeof document !== "undefined") {
    if (dialog.kind === "alert") {
      overlay = (
        <AlertView
          title={dialog.title}
          message={dialog.message}
          variant={dialog.variant}
          confirmLabel={dialog.confirmLabel}
          onClose={dialog.close}
        />
      );
    } else if (dialog.kind === "confirm") {
      overlay = (
        <ConfirmView
          title={dialog.title}
          message={dialog.message}
          confirmLabel={dialog.confirmLabel}
          cancelLabel={dialog.cancelLabel}
          danger={dialog.danger}
          onCancel={() => dialog.close(false)}
          onConfirm={() => dialog.close(true)}
        />
      );
    } else if (dialog.kind === "details") {
      overlay = (
        <DetailsView
          title={dialog.title}
          namePlaceholder={dialog.namePlaceholder}
          descriptionPlaceholder={dialog.descriptionPlaceholder}
          defaultName={dialog.defaultName}
          defaultDescription={dialog.defaultDescription}
          withDescription={dialog.withDescription}
          confirmLabel={dialog.confirmLabel}
          cancelLabel={dialog.cancelLabel}
          onCancel={() => dialog.close(null)}
          onConfirm={(v) => dialog.close(v)}
        />
      );
    } else if (dialog.kind === "prompt") {
      overlay = (
        <PromptView
          title={dialog.title}
          message={dialog.message}
          placeholder={dialog.placeholder}
          defaultValue={dialog.defaultValue}
          confirmLabel={dialog.confirmLabel}
          cancelLabel={dialog.cancelLabel}
          onCancel={() => dialog.close(null)}
          onConfirm={(v) => dialog.close(v)}
        />
      );
    }
  }

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      {overlay}
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const ctx = useContext(AppDialogContext);
  if (!ctx) {
    throw new Error("useAppDialog must be used within AppDialogProvider");
  }
  return ctx;
}
